import { useState, useEffect, useCallback, use } from 'react';
import { useSse } from '../../sse';
import GameOver from './GameOver';
import './HostQuizGamePlay.css';
import { startGame } from '../../apiservices/axiosservice/gameservice';
import { getSubscriber } from '../../apiservices/LocalStorageService';
import { getCurrentCounter } from '../../utils/timeCounter';
import {
  fetchGameStatusStream,
  fetchCurrentQuizStream,
  fetchQrCode,
} from '../../apiservices/axiosservice/gameservice';
import { toast } from 'react-toastify';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import Loading from '../admincomponents/components/Loading';
import MusicToggle from './MusicToggle';
import HostGameCode from './HostGameCode';

const optionBarColors = ['#4A5568', '#F5A623', '#4A5568', '#F5A623'];
const podiumColors = ['#8B7355', '#D4A843', '#8B7355'];

interface Props {
  onClose: () => void;
  gameId: string;
}

interface Option {
  optionId: string;
  answerText: string;
  color?: string;
}
interface Question {
  questionId: string;
  questionMessage: string;
  options: Option[];
  timeInterval: number;
  optionType: string;
  answerCount: number;
}

const HostQuizGamePlay: React.FC<Props> = ({ onClose, gameId }) => {
  const [timeLeft, setTimeLeft] = useState(10);
  const [revealed, setRevealed] = useState(false);
  const [optionResults, setOptionResults] = useState<OptionResult[]>([]);
  const [gameOver, setGameOver] = useState(false);
  const [question, setQuestion] = useState<Question | undefined>();
  const [quizPosition, setQuizPosition] = useState(1);
  const leaderboardEntry = useLeaderboard(gameId);

  const quizSync = useSse<QuizPayload>('quiz-sync');
  const quizResult = useSse<any>('quiz-result');
  const [quizResultData, setQuizResultData] = useState<any>();
  const [quizData, setQuizData] = useState<any>({
    quizPosition: 1,
    totalQuiz: 10,
    nextQuestion: { options: [], questionMessage: '' },
  });
  const [currentGameStatus, setCurrentGameStatus] = useState<GameStatusInfo>({
    gameId: '',
    gameType: '',
    status: 'IN_PROGRESS',
    totalRound: 10,
  });
  const gameStatus = useSse<any>('game-status');
  const user = getSubscriber();
  const [qrCode, setQrCode] = useState<string>('');
  const isCorrect = (answers: string[] = [], answerText: string) => {
    // for (single, multiple & true or false)
    // logic for free text to be added latercan

    answers.some((ans) => answerText.toLowerCase() === ans.toLowerCase());
  };

  // Game status
  useEffect(() => {
    fetchGameStatusStream(gameId)
      .then((res) => {
        setCurrentGameStatus(res.data);
        setGameOver(res.data.status === 'FINISHED');
      })
      .catch((error) => {
        console.log('Error fetching game status:', error);
      });
  }, []);

  // Fetch the join QR once per game
  useEffect(() => {
    if (!gameId) return;
    fetchQrCode(gameId)
      .then((res) => setQrCode(res.data.qrCodeBase64Image))
      .catch((error) => console.log('Error fetching QR code:', error));
  }, [gameId]);
  // Fetch when reload of browser is triggered
  useEffect(() => {
    if (gameId) {
      fetchCurrentQuizStream(gameId)
        .then((res) => {
          const currentQuiz = res?.data ?? res;

          setQuizData(currentQuiz);
          setQuestion(currentQuiz.nextQuestion);
          setQuizPosition(currentQuiz.quizPosition || 1);
          setTimeLeft(getCurrentCounter(currentQuiz.currentExpiryTime));
          setRevealed(false);
          setTimeLeft(getCurrentCounter(currentQuiz.currentExpiryTime));
        })
        .catch((err) => {
          toast.error(err.message);
        });
    }
  }, []);

  // SSE: new question
  // SSE: new question from host
  useEffect(() => {
    if (quizSync?.nextQuestion) {
      setQuizData(quizSync);
      setQuestion(quizSync.nextQuestion);
      setQuizPosition(quizSync.quizPosition);
      setTimeLeft(getCurrentCounter(quizSync.currentExpiryTime));
      setRevealed(false);
    }
  }, [quizSync]);

  // SSE: quiz result (answers revealed)
  useEffect(() => {
    if (quizResult) {
      setRevealed(true);
      setQuizResultData(quizResult);
      // quizResult.answers contains correct answer IDs from backend
    }
  }, [quizResult]);

  // SSE: game finished
  useEffect(() => {
    setCurrentGameStatus(gameStatus);
    if (gameStatus?.status === 'FINISHED') setGameOver(true);
  }, [gameStatus]);

  // Timer countdown — reveal when it hits 0
  useEffect(() => {
    if (timeLeft <= 0) {
      setRevealed(true);
      return;
    }
    const t = setInterval(() => setTimeLeft((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, [timeLeft, revealed]);

  const initiateGame = useCallback(async () => {
    const request: PreviewRemoteRequest = {
      gameId,
      gameOwnerId: user?.userId,
    };

    try {
      const response = await startGame(request);

      if (response.data) {
        setGameOver(response.data.status === 'FINISHED');
      }
    } catch (err) {
      console.log('Error starting game:', err);
    }
  }, []);

  const handleNextQuestion = () => {
    // if (quizData.quizPosition >= quizData.totalQuiz) {
    //   setGameOver(true);
    //   return;
    // }
    initiateGame();
    // Replace with API call to trigger next question
    setRevealed(false);
  };

  // const q = quizData.quizPosition;
  const timerPct = (timeLeft / question?.timeInterval ?? 60) * 100;
  const timerExpired = timeLeft === 0;

  if (gameOver) {
    return (
      <GameOver
        onPlayAgain={onClose}
        leaderBoard={leaderboardEntry}
        gameInfo={currentGameStatus}
      />
    );
  }

  const top3 = leaderboardEntry.slice(0, 3);
  // Podium order: 2nd, 1st, 3rd
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;

  const getOptionCount = (optionId: string) =>
    optionResults.find((r) => r.optionId === optionId)?.count ?? '';

  // if (!quizData) {
  //   return <Loading message="Loading..." />;
  // }

  return (
    <div className="hgp">
      <MusicToggle />
      <HostGameCode gameId={gameId} qrCode={qrCode} />
      <div className="hgp__main">
        <div className="hgp__topbar">
          <span className="hgp__q-counter">
            Question {quizData.quizPosition || 1} of {quizData.totalQuiz || 10}
          </span>
        </div>

        {/* Timer */}
        <div className="hgp__timer-wrap">
          <svg className="hgp__timer-svg" viewBox="0 0 100 100">
            <circle className="hgp__timer-bg" cx="50" cy="50" r="42" />
            <circle
              className={`hgp__timer-ring ${timerExpired ? 'hgp__timer-ring--expired' : ''}`}
              cx="50"
              cy="50"
              r="42"
              strokeDasharray={264}
              strokeDashoffset={264 - (264 * timerPct) / 100}
            />
          </svg>
          <span className="hgp__timer-text">{timeLeft}</span>
        </div>

        {/* Question */}
        <div className="hgp__question-card">
          <p className="hgp__question-text">
            {quizData.nextQuestion.questionMessage}
          </p>
        </div>

        {/* Options with counts when revealed */}
        <div className="hgp__options">
          {quizData.nextQuestion.options.map((o, i) => (
            <div
              key={o.optionId}
              className={`hgp__option ${revealed && isCorrect(quizResultData?.answers, o.answerText) ? 'hgp__option--correct' : ''}`}
            >
              <span
                className="hgp__option-label"
                style={{
                  background: optionBarColors[i % optionBarColors.length],
                }}
              >
                {o.answerText}
              </span>
              {revealed && (
                <span className="hgp__option-count">
                  {getOptionCount(o.optionId)}
                </span>
              )}
              <div className="hgp__option-bar" />
            </div>
          ))}
        </div>

        {/* Correct answer banner + Next button */}
        {revealed && (
          <>
            <div className="hgp__correct-banner">
              ✓ Correct Answer - {quizResultData?.answers?.join(', ')}
            </div>
            <div className="hgp__next-wrap">
              <button className="hgp__next-btn" onClick={handleNextQuestion}>
                Next Quiz
              </button>
            </div>
          </>
        )}
      </div>

      {/* Sidebar */}
      <div className="hgp__sidebar">
        <div className="hgp__lb-header">
          <span className="hgp__lb-icon">🏆</span>
          <h3 className="hgp__lb-title">Leaderboard</h3>
          <span className="hgp__lb-round">
            After Q{quizData.quizPosition} of {quizData.totalQuiz}
          </span>
        </div>

        {/* Podium — shown when scores exist */}
        {revealed && top3.length >= 3 && (
          <div className="hgp__podium">
            {podiumOrder.map((p, i) => {
              const place = podiumOrder.length >= 3 ? [2, 1, 3][i] : i + 1;
              return (
                <div key={p.playerName} className="hgp__podium-col">
                  <span
                    className="hgp__podium-avatar"
                    style={{ background: p.iconColor }}
                  >
                    {p.playerIcon}
                  </span>
                  <span className="hgp__podium-name">{p.playerName}</span>
                  <div
                    className={`hgp__podium-block hgp__podium-block--${place}`}
                    style={{ background: podiumColors[i] }}
                  >
                    <span className="hgp__podium-place">{place}</span>
                    <span className="hgp__podium-score">{p.totalPoints}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="hgp__lb-list">
          {leaderboardEntry.map((p) => (
            <div key={p.playerName} className="hgp__lb-row">
              <span className="hgp__lb-rank">#{p.rank}</span>
              <span
                className="hgp__lb-avatar"
                style={{ background: p.iconColor }}
              >
                {p.playerIcon}
              </span>
              <div className="hgp__lb-info">
                <span className="hgp__lb-name">{p.playerName}</span>
                {p.totalPoints > 0 && (
                  <span className="hgp__lb-pts">{p.totalPoints} pts</span>
                )}
              </div>
              <span className="hgp__lb-score">
                {p.totalPoints > 0 ? '' : '—'}
              </span>
            </div>
          ))}
        </div>

        {/* Answering status — hidden when revealed */}
        {!revealed && (
          <>
            <div className="hgp__answering">
              <span className="hgp__answering-dot" />
              <span className="hgp__answering-text">
                {leaderboardEntry.length} players answering
              </span>
            </div>
            <div className="hgp__answering-bars">
              {leaderboardEntry.map((p) => (
                <div
                  key={p.playerName}
                  className="hgp__answering-bar"
                  style={{ background: p.iconColor }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HostQuizGamePlay;
